/** EditOpportunityHeader.jsx - the editor's hero band: title, owner, meta cards, sales stages, tabs. */
import { GAT_ACCENT, SALES_STAGES } from './editOpportunityConstants';
import { Icon } from '../ui/Icon';

/** The editor's tabs, in order: [[id, label]]. A new record has no Tasks,
 *  Activity, Costs or project cost tabs yet (they hang on a saved record).
 *   showTimeline    Activity (AL-01) - false on the GLOBAL KSA tab, which has no CRM timeline
 *   showCosts       'Costs & profit' (r44) - a KSA opportunity (a KSA project has the panel in its hero)
 *   showProjectCosts the Tracking tab (a main-tab project)
 *   (the project Timesheets tab - ADP hours - is gone, 10/12/26: hours are
 *   typed per engineer and day on Tracking > Log time & expenses, as on the tracking workbook) */
export function editorTabs({
  isCreate,
  isProject = false,
  showTimeline,
  showCosts,
  showProjectCosts = false,
  showDailies = false,
  familyLabel = null,
}) {
  return [
    ['summary', 'Summary'],
    ['quote', isProject ? 'Project Quote' : 'Opportunity Quote'],
    ...(!isCreate && showCosts ? [['costs', 'Costs & profit']] : []),
    // a main-tab project's Tracking (components/projectCosts, 10/14/26): one
    // tab - Summary, Log time & expenses, Materials, Setup - in place of the
    // five of 10/12/26 (Budget, Time & cost, Expenses, Equipment & Materials,
    // COGS). Cost and margin inside it are the 'cost-rates' area's - the
    // server leaves them out of its answer for everyone else.
    ...(!isCreate && showProjectCosts ? [['tracking', 'Tracking']] : []),
    // the historical daily report emails filed under this project's numbers (11/2/26)
    ...(!isCreate && showDailies ? [['dailies', 'Daily reports']] : []),
    // main project / sub-project / PO line (10/14/26): its children (linked by hand) and its family
    ...(!isCreate && familyLabel ? [['family', familyLabel]] : []),
    ['attachments', 'Doc Hub'],
    ...(isCreate ? [] : [['tasks', 'Tasks']]),
    ...(!isCreate && showTimeline ? [['timeline', 'Activity']] : []),
  ];
}

/**
 * Where the record sits (10/14/26): "Main project P26006 · Oshkosh ... › Sub-project
 * P26006-030 (line 2 of 3)". Each part opens its page (onFamily('main' | 'sub')).
 */
function FamilyCrumb({ family, onFamily }) {
  const link = {
    border: 0,
    background: 'none',
    padding: 0,
    cursor: onFamily ? 'pointer' : 'default',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 700,
    color: '#1B5E20',
    textDecoration: onFamily ? 'underline' : 'none',
  };
  const tag = { fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 10, fontWeight: 700 };
  const m = family.main;
  return (
    <span
      data-testid="family-crumb"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        background: '#F1F6EB',
        border: '1px solid #DCE8D2',
        borderRadius: 12,
        padding: '2px 10px',
        fontSize: 11,
      }}
    >
      {family.isMainRecord ? (
        <span style={{ fontWeight: 800, color: '#1B5E20' }}>Main project · {m.members.length - 1} under it</span>
      ) : (
        <>
          <span style={{ color: '#666', fontWeight: 600 }}>Main project</span>
          <button type="button" style={link} onClick={() => onFamily && onFamily('main')}>
            {m.gpn && <span style={tag}>{m.gpn} </span>}
            {m.label || 'open'}
          </button>
          {family.sub && String(family.sub.head.id) !== String(family.self) && (
            <>
              <span style={{ color: '#aaa' }}>›</span>
              <button type="button" style={link} onClick={() => onFamily && onFamily('sub')}>
                {family.sub.gpn ? <span style={tag}>{family.sub.gpn}</span> : family.sub.label || 'Sub-project'}
              </button>
              <span style={{ color: '#666' }}>({family.sub.records.length} records)</span>
            </>
          )}
        </>
      )}
    </span>
  );
}

const badgeSt = (bg) => ({
  marginLeft: 6,
  background: bg,
  color: '#fff',
  borderRadius: 10,
  padding: '1px 7px',
  fontSize: 9,
  fontWeight: 800,
});

/* One record, two stages (an opportunity until Won, then a project):
     isProject   the form's Status is Won / Complete - "Project" in the
                 eyebrow, no sales stages (an opportunity's)
     metaCards   [{ label, value, color, mono }] in place of the
                 opportunity's four (Account, Est. Close, Est. Revenue, Margin)
     pills       the Status / Active-Closed pills beside the owner
     linkedFrom  the opportunity a project was made from, when its
                 description says so
     actuals     the band under the cards (a project's actuals, or a KSA
                 project's costs and profit)
     tabs        editorTabs(...) */
export function renderHeroHeader({
  isCreate,
  opp,
  f,
  setF,
  tab,
  setTab,
  ownerName,
  grossMarginPct,
  marginColor,
  metaAccount,
  metaEstClose,
  metaEstRevenue,
  docHubCount,
  showTimeline,
  showCosts,
  isProject = false,
  metaCards = null,
  pills = null,
  linkedFrom = null,
  actuals = null,
  tabs = null,
  family = null,
  familyActions = null,
  onFamily = null,
}) {
  const noun = isProject ? 'Project' : 'Opportunity';
  const cards = metaCards || [
    { label: 'Account', value: metaAccount, color: '#0D0D0D' },
    { label: 'Est. Close Date', value: metaEstClose, color: '#0D0D0D', mono: true },
    { label: 'Est. Revenue', value: metaEstRevenue, color: GAT_ACCENT, mono: true },
    { label: 'Margin %', value: grossMarginPct != null ? `${grossMarginPct.toFixed(2)}%` : '-', color: marginColor },
  ];
  const tabList = tabs || editorTabs({ isCreate, isProject, showTimeline, showCosts });
  return (
    <div
      style={{ padding: '14px 24px 0', background: 'linear-gradient(135deg, #fff 0%, #F9FBF7 100%)', borderBottom: '1px solid #ECECEC' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 10, fontWeight: 800, color: GAT_ACCENT, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 }}
          >
            {isCreate ? `New ${noun}` : `${noun} · ${opp.quoteLetter || opp.quote_letter || opp.id}`}
          </div>
          <div style={{ fontSize: 19, fontWeight: 900, color: '#0D0D0D', marginBottom: 4, lineHeight: 1.2 }}>
            {f.name || (isCreate ? `New ${noun}` : opp.description || 'Untitled')}
          </div>
          {(f.customer || linkedFrom) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {f.customer && <span style={{ fontSize: 12, fontWeight: 700, color: GAT_ACCENT }}>{f.customer}</span>}
              {f.subCustomer && (
                <>
                  <span style={{ fontSize: 11, color: '#ccc' }}>·</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>{f.subCustomer}</span>
                </>
              )}
              {family && <FamilyCrumb family={family} onFamily={onFamily} />}
              {linkedFrom && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#E8F5E9',
                    color: '#1B5E20',
                    fontSize: 9.5,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 12,
                    border: '1px solid #C8E6C9',
                  }}
                >
                  <Icon name="link" size={9} color="#1B5E20" />
                  From: {linkedFrom}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          {pills && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{pills}</div>}
          {/* Owner avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: GAT_ACCENT,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              {ownerName
                ? ownerName
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()
                : '-'}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>{ownerName || 'Unassigned'}</div>
              <div style={{ fontSize: 9.5, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Owner</div>
            </div>
          </div>
        </div>
      </div>

      {familyActions}

      {/* Meta card strip */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, 1fr)`, gap: 10, marginTop: 10 }}>
        {cards.map((m) => (
          <div key={m.label} style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 8, padding: '5px 12px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 1 }}>
              {m.label}
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: m.color,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: m.mono ? "'JetBrains Mono', Consolas, monospace" : undefined,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {actuals}

      {/* Sales Process stages - an opportunity's; a project has none */}
      {!isProject && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1.4, textTransform: 'uppercase', marginRight: 8 }}>
            Sales Stage
          </span>
          {SALES_STAGES.map((s, idx) => {
            const isActive = s === f.salesStage;
            const isPast = SALES_STAGES.indexOf(f.salesStage) > idx;
            const filled = isActive || isPast;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setF((p) => ({ ...p, salesStage: s }))}
                style={{
                  flex: 1,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  background: isActive ? GAT_ACCENT : filled ? '#E8F5E9' : '#fff',
                  color: isActive ? '#fff' : filled ? GAT_ACCENT : '#888',
                  border: `1px solid ${isActive ? GAT_ACCENT : '#D8DED4'}`,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "'Montserrat', sans-serif",
                  borderRadius: 999,
                  transition: 'all 0.15s',
                }}
              >
                {idx + 1}. {s}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab bar. Doc Hub is available while creating: the RFQ that started
          the opportunity usually arrives before the record exists. Files are
          held in the browser and uploaded the moment the save returns an id.
          Tasks still waits - a task needs a record to be assigned against,
          and so does Activity (the entries tagged to it and its own events). */}
      <div data-hero-tabs="" style={{ marginTop: 8, display: 'flex', gap: 22 }}>
        {tabList.map(([id, label]) => (
          <div
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '8px 0',
              cursor: 'pointer',
              fontSize: 12.5,
              fontWeight: 700,
              color: tab === id ? GAT_ACCENT : '#888',
              borderBottom: `2px solid ${tab === id ? GAT_ACCENT : 'transparent'}`,
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: 0.3,
            }}
          >
            {label}
            {id === 'attachments' && docHubCount > 0 && <span style={badgeSt('#E65100')}>{docHubCount}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
